import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon, ChatBubbleOutline as CommentIcon } from '@mui/icons-material';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { SegmentService } from '../../../services/segment.service';
import { Segment } from '../../../interfaces/accounting.interface';
import DepartmentPicker, { useDepartmentOptions } from '../../costcentre/DepartmentPicker'; // B4-H
import ProjectSelect, { useProjectOptions } from '../../project/ProjectSelect'; // P1-A project
import { AuthService } from '../../../services/auth.service'; // B4-H
import OrderPicker from '../../costing/OrderPicker';
import { BudgetVersion, FpaService, VarianceParams, VarianceReport, VarianceRow } from '../../../services/fpa.service';
import SearchableSelect from '../../common/SearchableSelect';
import { firstOfYearLocalIso, toLocalIsoDate } from '../../../utils/dateUtils';
import { formatMoney } from '../../../utils/format';

const pct = (v: number | null) => (v === null || v === undefined ? 'n/a' : `${v.toFixed(2)}%`);

const BudgetVarianceReview: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.BUDGET_MANAGE);
    const canCosting = usePermission(PERMISSIONS.COSTING_VIEW);   // the order filter reads order-tagged costing lines
    const [versions, setVersions] = useState<BudgetVersion[]>([]);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [versionId, setVersionId] = useState('');
    const [from, setFrom] = useState(firstOfYearLocalIso());
    const [to, setTo] = useState(toLocalIsoDate());
    const [segmentId, setSegmentId] = useState('');
    const [orderId, setOrderId] = useState('');
    const [departmentId, setDepartmentId] = useState(''); // B4-H
    const departmentOptions = useDepartmentOptions(AuthService.getSelectedInstitution()); // B4-H
    const [projectId, setProjectId] = useState(''); // P1-A project
    const projectOptions = useProjectOptions(AuthService.getSelectedInstitution()); // P1-A project
    const [applied, setApplied] = useState<VarianceParams | null>(null);
    const [report, setReport] = useState<VarianceReport | null>(null);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState<VarianceRow | null>(null);
    const [cMonth, setCMonth] = useState('');
    const [cText, setCText] = useState('');

    useEffect(() => {
        FpaService.listVersions().then(v => { setVersions(v); if (v.length && !versionId) setVersionId(v[0].id); })
            .catch(err => toast.showApiError(err, 'Failed to load budget versions'));
        SegmentService.getByInstitution(0, 200).then(p => setSegments(p.content ?? p)).catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const apply = async () => {
        if (!versionId) return;
        const p: VarianceParams = { versionId, from, to, segmentId: segmentId || undefined, buyerOrderId: orderId || undefined, departmentId: departmentId || undefined, projectId: projectId || undefined };
        try { setLoading(true); setReport(await FpaService.getVariance(p)); setApplied(p); }
        catch (err) { toast.showApiError(err, 'Failed to load the variance report'); }
        finally { setLoading(false); }
    };

    // Downloads use the APPLIED filters, never unapplied inputs.
    const download = async (format: 'pdf' | 'xlsx') => {
        if (!applied) return;
        try { await FpaService.downloadVariance(applied, format); } catch (err) { toast.showApiError(err, 'Download failed'); }
    };

    const openComment = (r: VarianceRow) => {
        const end = new Date((applied?.to ?? to) + 'T00:00:00');
        setEditing(r); setCMonth(`${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}`);
        setCText(r.comments.find(c => `${c.year}-${String(c.month).padStart(2, '0')}` === `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}`)?.comment ?? '');
    };

    const saveComment = async () => {
        if (!editing || !applied) return;
        const [y, m] = cMonth.split('-').map(Number);
        try {
            await FpaService.saveComment({ versionId: applied.versionId, accountId: editing.accountId, year: y, month: m, segmentId: applied.segmentId, comment: cText });
            toast.showSuccess('Comment saved'); setEditing(null); await apply();
        } catch (err) { toast.showApiError(err, 'Could not save the comment'); }
    };

    return (
        <Box data-help-page="accounting.variance.page" sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Budget Variance Review</Typography>
            <Box data-help-section="accounting.variance.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 3 }}>
                <Box sx={{ flex: '1 1 240px', maxWidth: 320 }}>
                    <SearchableSelect data-help="accounting.variance.version" options={versions} value={versionId} onChange={setVersionId} getOptionLabel={v => `${v.name} (${v.status})`} label="Budget version" size="small" clearable={false} />
                </Box>
                <TextField data-help="accounting.variance.from" label="From" type="date" size="small" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 190 }} />
                <TextField data-help="accounting.variance.to" label="To" type="date" size="small" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 190 }} />
                <Box sx={{ flex: '1 1 200px', maxWidth: 260 }}>
                    <SearchableSelect data-help="accounting.variance.segment" options={segments} value={segmentId} onChange={setSegmentId} getOptionLabel={s => `${s.code} ${s.name}`} label="Segment" size="small" />
                </Box>
                {departmentOptions.length > 0 && (
                    <Box data-help="accounting.variance.department" sx={{ flex: '1 1 200px', maxWidth: 260 }}>{/* B4-H */}
                        <DepartmentPicker options={departmentOptions} value={departmentId} onChange={setDepartmentId} label="Department" size="small"
                            placeholder="All" disabled={Boolean(orderId) || Boolean(projectId)} />
                    </Box>
                )}
                {projectOptions.length > 0 && (
                    <Box data-help="accounting.variance.project" sx={{ flex: '1 1 200px', maxWidth: 260 }}>{/* P1-A project: one of order, department or project */}
                        <ProjectSelect options={projectOptions} value={projectId} label="Project" size="small" placeholder="All"
                            disabled={Boolean(orderId) || Boolean(departmentId)} data-testid="variance-project"
                            onChange={id => { setProjectId(id); if (id) { setDepartmentId(''); setOrderId(''); } }} />
                    </Box>
                )}
                {canCosting && (
                    <Box data-help="accounting.variance.order" sx={{ flex: '1 1 200px', maxWidth: 260 }}>
                        {/* searched on the server as the user types, never pre-loaded */}
                        <OrderPicker always openOnly={false} value={orderId} onChange={id => { setOrderId(id); if (id) { setDepartmentId(''); setProjectId(''); } }} label="Order" />
                    </Box>
                )}
                <Button data-help="accounting.variance.apply" variant="contained" onClick={apply} disabled={loading || !versionId}>{loading ? 'Loading...' : 'Apply'}</Button>
                <Button data-help="accounting.variance.pdf" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => download('pdf')} disabled={!applied}>PDF</Button>
                <Button data-help="accounting.variance.excel" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => download('xlsx')} disabled={!applied}>Excel</Button>
            </Box>

            {report && (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
                        <Typography variant="body2">Revenue: budget {formatMoney(report.totalBudgetRevenue)}, actual {formatMoney(report.totalActualRevenue)}</Typography>
                        <Typography variant="body2">Expense: budget {formatMoney(report.totalBudgetExpense)}, actual {formatMoney(report.totalActualExpense)}</Typography>
                        <Typography variant="body2">Profit: budget {formatMoney(report.budgetProfit)}, actual {formatMoney(report.actualProfit)}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">Variance = actual - budget. Flagged when over {report.thresholdPct}% and {formatMoney(report.minAmount)}.</Typography>
                    {report.notes.map(n => <Alert key={n} severity="info" sx={{ my: 1 }}>{n}</Alert>)}
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1.5 }}>
                        <Table data-help-section="accounting.variance.table" size="small" sx={{ minWidth: 820 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="accounting.variance.col.account">Account</TableCell><TableCell data-help="accounting.variance.col.budget" align="right">Budget</TableCell><TableCell data-help="accounting.variance.col.actual" align="right">Actual</TableCell>
                                    <TableCell data-help="accounting.variance.col.variance" align="right">Variance</TableCell><TableCell data-help="accounting.variance.col.pct" align="right">%</TableCell><TableCell data-help="accounting.variance.col.commentary">Commentary</TableCell><TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.rows.map(r => (
                                    <TableRow key={r.accountId} hover sx={r.flagged ? { bgcolor: 'warning.light' } : undefined}>
                                        <TableCell>{r.accountCode} {r.accountName} {r.unbudgeted && <Chip size="small" label="Unbudgeted" sx={{ ml: 0.5 }} />}</TableCell>
                                        <TableCell align="right">{formatMoney(r.budget)}</TableCell>
                                        <TableCell align="right">{formatMoney(r.actual)}</TableCell>
                                        <TableCell align="right">{formatMoney(r.variance)}</TableCell>
                                        <TableCell align="right">{pct(r.variancePct)}</TableCell>
                                        <TableCell>{r.comments.map(c => <div key={c.id}><b>{c.month}/{c.year}</b> {c.comment}</div>)}</TableCell>
                                        <TableCell>{canManage && <Tooltip title="Add or edit commentary"><IconButton data-help="accounting.variance.action.comment" size="small" aria-label={`Comment ${r.accountCode}`} onClick={() => openComment(r)}><CommentIcon fontSize="small" /></IconButton></Tooltip>}</TableCell>
                                    </TableRow>
                                ))}
                                {report.rows.length === 0 && <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">Nothing to compare in this range.</Typography></TableCell></TableRow>}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            <Dialog data-help-page="accounting.variance.comment.page" open={editing !== null} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Commentary - {editing?.accountCode} {editing?.accountName}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField data-help="accounting.variance.comment.field.month" select label="Month" size="small" value={cMonth} onChange={e => {
                            setCMonth(e.target.value);
                            const [y, m] = e.target.value.split('-').map(Number);
                            setCText(editing?.comments.find(c => c.year === y && c.month === m)?.comment ?? '');
                        }}>
                            {monthOptions(applied?.from ?? from, applied?.to ?? to).map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                        </TextField>
                        <TextField data-help="accounting.variance.comment.field.text" label="Comment (leave blank to clear)" multiline minRows={3} value={cText} onChange={e => setCText(e.target.value)} inputProps={{ maxLength: 1000 }} />
                    </Box>
                </DialogContent>
                <DialogActions><Button data-help="accounting.variance.comment.cancel" onClick={() => setEditing(null)}>Cancel</Button><Button data-help="accounting.variance.action.saveComment" variant="contained" onClick={saveComment}>Save</Button></DialogActions>
            </Dialog>
        </Box>
    );
};

const monthOptions = (from: string, to: string): string[] => {
    const out: string[] = [];
    const [fy, fm] = from.split('-').map(Number);
    const [ty, tm] = to.split('-').map(Number);
    for (let y = fy, m = fm; y < ty || (y === ty && m <= tm); m === 12 ? (y++, m = 1) : m++) {
        out.push(`${y}-${String(m).padStart(2, '0')}`);
        if (out.length > 60) break;
    }
    return out;
};

export default BudgetVarianceReview;
